import { getImpersonationCookie } from '../../utils/impersonation';
import { getJWTToken } from '../../utils/jwt';

const IMPERSONATE_ID_HEADER = 'X-Impersonate-Id';
const IMPERSONATE_ID_QUERY_PARAM = 'impersonateId';

export const normalizeUrl = (url: string | undefined): string => {
  if (!url) return '';
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
};

export const addProtocol = (url: string): string => {
  if (!url) return '';
  if (import.meta.env.DEV && (url.startsWith('localhost') || url.startsWith('127.0.0.1'))) {
    return `http://${url}`;
  }
  return url.startsWith('http') ? url : `https://${url}`;
};

export const getStreamingBaseUrl = (): string => {
  const candidates = [
    import.meta.env?.VITE_TINY_AI_STREAMING_URL,
    typeof process !== 'undefined' ? process.env?.VITE_TINY_AI_STREAMING_URL : undefined,
    typeof process !== 'undefined' ? process.env?.TINY_AI_STREAMING_URL : undefined,
  ].filter((value): value is string => !!value && !value.includes('url-unavailable'));

  const rawUrl = candidates[0] || (import.meta.env.DEV ? 'http://localhost:3010/trpc' : undefined);

  if (!rawUrl) {
    console.warn('VITE_TINY_AI_STREAMING_URL is not configured; TinyAI chat requests will fail');
    return '';
  }

  const normalized = addProtocol(normalizeUrl(rawUrl));
  return normalized.endsWith('/trpc') ? normalized : `${normalized}/trpc`;
};

export const getStreamingEventSourceUrl = () => {
  const baseUrl = getStreamingBaseUrl();

  if (!baseUrl || typeof window === 'undefined') {
    return baseUrl;
  }

  const token = getJWTToken();
  const impersonateId = getImpersonationCookie();

  if (!token && !impersonateId) {
    return baseUrl;
  }

  try {
    const url = new URL(baseUrl);
    if (token) url.searchParams.set('token', token);
    if (impersonateId)
      url.searchParams.set(IMPERSONATE_ID_QUERY_PARAM, impersonateId);
    return url.toString();
  } catch (err) {
    console.warn('Invalid streaming URL configuration; using base string', { baseUrl, err });
    const separator = baseUrl.includes('?') ? '&' : '?';
    const params = new URLSearchParams();
    if (token) params.set('token', token);
    if (impersonateId) params.set(IMPERSONATE_ID_QUERY_PARAM, impersonateId);
    return `${baseUrl}${separator}${params.toString()}`;
  }
};

export const getAuthHeaders = (): Record<string, string> => {
  const token = getJWTToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const impersonateId = getImpersonationCookie();
  if (impersonateId) {
    headers[IMPERSONATE_ID_HEADER] = impersonateId;
  }
  return headers;
};
